import { useState, useEffect, useCallback } from 'react'
import { api } from '@/lib/api'
import type {
    AvailabilityExceptionInput,
    AvailabilityWindowInput,
    MyProfessionalRecord,
    PackageServiceAssignment,
    ProfessionalAvailability,
    ServiceAssignmentInput,
    StudioProfessionalListItem,
    UpsertProfessionalProfileInput,
} from '@/lib/api'

function useApiData<T>(fetcher: () => Promise<T>, deps: unknown[] = [], enabled = true) {
    const [data, setData] = useState<T | null>(null)
    const [isLoading, setIsLoading] = useState(enabled)
    const [error, setError] = useState<Error | null>(null)

    const refetch = useCallback(async () => {
        if (!enabled) {
            setData(null)
            setIsLoading(false)
            return
        }
        setIsLoading(true)
        setError(null)
        try {
            const result = await fetcher()
            setData(result)
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Unknown error'))
        } finally {
            setIsLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, ...deps])

    useEffect(() => {
        refetch()
    }, [refetch])

    return { data, isLoading, error, refetch }
}

export function useStudioProfessionals(enabled = true) {
    return useApiData<{ professionals: StudioProfessionalListItem[] }>(
        () => api.getProfessionals(),
        [],
        enabled,
    )
}

export function useMyProfessional() {
    return useApiData<{ professional: MyProfessionalRecord | null }>(
        () => api.getMyProfessional(),
    )
}

export function usePackageAssignments(packageId: string | null) {
    return useApiData<{ assignments: PackageServiceAssignment[] }>(
        () => api.getPackageAssignments(packageId as string),
        [packageId],
        Boolean(packageId),
    )
}

export function useProfessionalAvailability(userId: string | null) {
    return useApiData<ProfessionalAvailability>(
        () => api.getProfessionalAvailability(userId as string),
        [userId],
        Boolean(userId),
    )
}

export function useProfessionalMutations() {
    const [isLoading, setIsLoading] = useState(false)

    const execute = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
        setIsLoading(true)
        try {
            return await fn()
        } finally {
            setIsLoading(false)
        }
    }, [])

    return {
        isLoading,
        upsertProfile: (userId: string, data: UpsertProfessionalProfileInput) =>
            execute(() => api.upsertProfessionalProfile(userId, data)),
        requestPublication: (userId: string) =>
            execute(() => api.requestProfilePublication(userId)),
        unpublish: (userId: string) =>
            execute(() => api.unpublishProfessionalProfile(userId)),
        respondConsent: (action: 'accept' | 'decline') =>
            execute(() => api.respondProfessionalConsent(action)),
        setAssignments: (packageId: string, assignments: ServiceAssignmentInput[]) =>
            execute(() => api.setPackageAssignments(packageId, assignments)),
        addPortfolioItem: (userId: string, data: { imageUrl: string; imagePublicId: string; caption?: string | null }) =>
            execute(() => api.addProfessionalPortfolioItem(userId, data)),
        removePortfolioItem: (userId: string, itemId: string) =>
            execute(() => api.removeProfessionalPortfolioItem(userId, itemId)),
        reorderPortfolio: (userId: string, orderedItemIds: string[]) =>
            execute(() => api.reorderProfessionalPortfolio(userId, orderedItemIds)),
        setAvailabilityWindows: (userId: string, windows: AvailabilityWindowInput[]) =>
            execute(() => api.setProfessionalAvailabilityWindows(userId, windows)),
        addAvailabilityException: (userId: string, data: AvailabilityExceptionInput) =>
            execute(() => api.createProfessionalAvailabilityException(userId, data)),
        updateAvailabilityException: (
            userId: string,
            exceptionId: string,
            data: Partial<AvailabilityExceptionInput>,
        ) => execute(() => api.updateProfessionalAvailabilityException(userId, exceptionId, data)),
        deleteAvailabilityException: (userId: string, exceptionId: string) =>
            execute(() => api.deleteProfessionalAvailabilityException(userId, exceptionId)),
    }
}
